import { inject, Injectable, linkedSignal } from '@angular/core';
import { ROUTES } from '@angular/router';
import { isNotNullish, requiredValue } from '@nestech/utils';
import { FeatureService, MemberStore } from '@web/service';
import { ExtendedRoute, findArea, MenuItemConfig, resolveMenuItemState } from '../../../routes/routes.model';
import { SideNavigationMenuItem } from '../model/side-navigation.model';
import { SideNavigationRouteMenuItemState } from './side-navigation-route-menu-item-state';

interface ExtendedRouteWithMenu extends ExtendedRoute {
  menuItem: MenuItemConfig;
}

export const flattenRoutes = (routes: ExtendedRoute[], parentPath = ''): ExtendedRouteWithMenu[] => {
  return routes
    .flatMap((route: ExtendedRoute) => {
      const currentPath = [parentPath, route.path].filter(Boolean).join('/');

      const routeInfo: ExtendedRoute = {
        ...route,
        path: currentPath,
      };

      return route.children ? [routeInfo, ...flattenRoutes(route.children, currentPath)] : [routeInfo];
    })
    .filter(route => route.menuItem) as ExtendedRouteWithMenu[];
};

@Injectable()
export class SideNavigationService {
  #memberStore = inject(MemberStore);
  #feature = inject(FeatureService);
  #menuState = inject(SideNavigationRouteMenuItemState);
  routes = flattenRoutes(inject(ROUTES).flat() as ExtendedRoute[]);

  menuItems = linkedSignal<SideNavigationMenuItem[] | null, SideNavigationMenuItem[]>({
    source: (): SideNavigationMenuItem[] | null => {
      const member = this.#memberStore.member.value();
      const policyState = this.#memberStore.policyState();
      const getMenuState = this.#menuState.menuItemStates();

      if (!member || !policyState) return null;

      const params = {
        policyState,
        hasDisbursements: !!this.#memberStore.hasDisbursements(),
      };
      const toReturn = this.routes
        .filter((r: ExtendedRouteWithMenu): r is Required<ExtendedRouteWithMenu> => isNotNullish(r.path))
        .map<SideNavigationMenuItem>((route, index) => {
          const menuItem = route.menuItem;
          const path = requiredValue(route.path, 'Route path');
          return {
            ...menuItem,
            routerLink: [path],
            divider: menuItem.dividerFn?.(params, this.#feature),
            state: resolveMenuItemState(findArea(path, member.access.areas), getMenuState(path)),
            priority: menuItem.priority ?? index,
          };
        })
        .filter(r => r.state !== 'hidden')
        .sort((a, b) => a.priority - b.priority);
      return toReturn;
    },
    computation: (next, previous) => next ?? previous?.value ?? [],
  });
}
